/**
 * SC-247: the in-room bottom bar (mic/video controls + connecting placeholder) mounts as soon as
 * the room is joined — deliberately NOT gated on `!isLoading`. On slow links `isLoading`
 * (`loadingGame`, the final load-pipeline step) stays true long after `roomJoined`, which used to
 * hide the mic/video control bar for seconds-to-minutes. Mounting at `roomJoined` lets the lazy
 * GameMenuController chunk download during the loading phase so the bar is ready the moment the
 * loading screen clears.
 *
 * SC-342: whiteboard is now an overlay modal inside LiveKit, so it no longer hides the bottom bar.
 */
export function shouldMountInRoomBottomBar(params: {
  roomJoined: boolean
}): boolean {
  return params.roomJoined
}

/**
 * SC-439: the Admin Panel mounts only for an admin the server has verified, never from the
 * client's own role string, so it agrees with the admin items in the game menu.
 */
export function shouldMountPreLoadAdminController(params: { verifiedAdmin: boolean }): boolean {
  return params.verifiedAdmin
}

export function shouldMountInRoomAdminController(params: {
  verifiedAdmin: boolean
  roomJoined: boolean
  isLoading: boolean
}): boolean {
  return params.verifiedAdmin && params.roomJoined && !params.isLoading
}

/**
 * SC-473: the company chat panel is mounted once at app level (one Matrix client) so the
 * dashboard's Chat page and the in-game popup share it.
 * - In a room (loaded) it is mounted for everyone, guests included, exactly as before.
 * - Outside a room (dashboard) it is for signed-in, non-guest users only.
 * Not gated on `isLoading` outside a room, and once mounted it stays mounted through the
 * dashboard-to-room hop for signed-in users, so the Matrix session isn't dropped.
 *
 * This gate is for the company chat. The planned in-room chat for meetings and screenshare is a
 * separate component that must be available to everyone, so it must not reuse this guest rule.
 */
export function shouldMountChatPanel(params: {
  authenticated: boolean
  isGuest: boolean
  roomJoined: boolean
  isLoading: boolean
}): boolean {
  if (params.authenticated && !params.isGuest) return true
  return params.roomJoined && !params.isLoading
}

/**
 * Sections that show the "company disabled" screen when the legacy cloud-error detection fires
 * (the company-access check failed open). `chat` is included: a suspended Parse-backend company
 * would otherwise still get a working docked chat.
 */
export function isCompanyGatedSection(section: string): boolean {
  return section === 'publicLobby' || section === 'myRooms' || section === 'meetings' || section === 'chat'
}
